/**
 * masterGanttRowBuilder.ts
 * Phase 4, Item 1 — Pure function that converts a MasterGanttResponse
 * into Highcharts Gantt { categories, series } config.
 * Phase 4, Item 2 — Full color/fill semantics via ganttFillResolver.
 *
 * Row hierarchy (Highcharts treegrid via `id` / `parent` on each point) — the
 * project-grouped tree is built in masterGanttProjectRows.ts (BUG-2026-0194
 * added per-machine / per-test-type and per-person drill-down levels; weekly
 * assignment rows are merged per task by masterGanttAssignmentMerge.ts):
 *
 *   Project row              id = "proj-{projectKey}"
 *     └─ WorkOrder row       id = "wo-{workOrderKey}"
 *          ├─ Machines group id = "machines-{woKey}"
 *          │    └─ Machine   id = "machsum-{woKey}-{machineKey}"        (collapsed)
 *          │         └─ Test type id = "machtt-{woKey}-{machineKey}-{slug}" (collapsed, >1 booking)
 *          │              └─ Booking id = "booking-{bookingKey}"
 *          └─ Staff group    id = "staff-{woKey}"
 *               └─ Person    id = "person-{woKey}-{userKey}"           (collapsed)
 *                    └─ Task bar id = "assign-{firstWeeklyKey}"        (weeks merged)
 *
 * This file keeps the shared LEAF bar builders (buildBookingPoint /
 * buildAssignmentPoint), the date helpers, the critical-path computation and
 * the buildGanttChartData entry point used by both group-by layouts.
 *
 * Color strategy (using theme.schedule.* tokens passed in as `colors`):
 *   - Project / WO envelope bars  → thin outline envelope colors (sage tones)
 *   - Machine Time bars           → machineTime (slate blue); hatched if Tentative
 *   - Employee Allocation bars    → employeeAlloc (moss green); hatched if Tentative
 *   - Committed bars stay solid; Tentative bars get diagonal-stripe pattern + 60% opacity
 *   - Maintenance / Calibration   → maintenance (amber)
 *   - Out of Service              → outOfService (grey-brown)
 *
 * Each GanttPoint is tagged with:
 *   taskType  — 'project' | 'workOrder' | 'machinesGroup' | 'staffGroup'
 *               | 'machineBooking' | 'employeeAssignment'
 *   status    — 'Tentative' | 'Committed' | null (null = envelope/group rows)
 *   eventType — 'Maintenance' | 'Calibration' | 'OutOfService' | null
 *
 * Notes:
 *   - Highcharts Gantt expects x/x2 as Unix milliseconds (UTC midnight).
 *   - The `name` on a series point is shown in the tooltip and left-axis label.
 *   - `collapsed: false` means the node starts expanded.
 */

import type {
  MasterGanttResponse,
  MasterGanttMachineBooking,
  MasterGanttEmployeeAssignment,
} from '../../types/schedule.js';

import {
  resolvePointFill,
  type GanttFill,
  type GanttTaskType,
  type GanttStatus,
  type GanttEventType,
  type FillTokens,
} from './ganttFillResolver.js';

import { buildMachineGroupedPoints } from './masterGanttMachineRows.js';
import { buildProjectPoints } from './masterGanttProjectRows.js';
import { shareSuffix } from './masterGanttAssignmentMerge.js';

// ---------------------------------------------------------------------------
// Highcharts point shape (minimal — Highcharts types are flexible)
// ---------------------------------------------------------------------------

export interface GanttPoint {
  id: string;
  parent?: string;
  name: string;
  start?: number;          // Unix ms
  end?: number;            // Unix ms
  /**
   * Highcharts `color` — may be a hex string (solid) or a
   * HighchartsPatternFill object (hatched for Tentative bars).
   * Typed as unknown so Highcharts accepts it without TS overload issues.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  color?: any;
  opacity?: number;
  collapsed?: boolean;
  /** Phase 4 item 2 — row type discriminator for color resolution. */
  taskType: GanttTaskType;
  /** Phase 4 item 2 — booking/assignment status; null for envelope/group rows. */
  status: GanttStatus;
  /** Phase 4 item 2 — event override; null for normal task bars. */
  eventType: GanttEventType;
  /** Extension surface for item 3+ (conflict markers, deep-links, etc.) */
  custom?: Record<string, unknown>;
}

export interface GanttSeries {
  name: string;
  data: GanttPoint[];
}

export interface GanttChartData {
  series: GanttSeries[];
}

// ---------------------------------------------------------------------------
// Theme color bag (passed in so the builder stays pure / theme-free)
// ---------------------------------------------------------------------------

export interface GanttColorTokens extends FillTokens {
  // FillTokens already carries all required fields:
  //   committed, tentative, machineTime, employeeAlloc,
  //   maintenance, outOfService, projectEnvelope, workOrderEnvelope
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Milliseconds in one calendar day. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/** 'YYYY-MM-DD' → Unix milliseconds (UTC midnight). */
export function toMs(isoDate: string | null | undefined): number | undefined {
  if (!isoDate) return undefined;
  return Date.UTC(
    parseInt(isoDate.slice(0, 4), 10),
    parseInt(isoDate.slice(5, 7), 10) - 1,
    parseInt(isoDate.slice(8, 10), 10),
  );
}

/**
 * BUG-2026-0095 — End edge of a Gantt bar, in Unix ms.
 *
 * Booking / assignment / envelope dates are *inclusive* last days
 * ('YYYY-MM-DD' at UTC midnight). Highcharts Gantt treats a point's `end`
 * (its `x2`) as the EXCLUSIVE right edge of the bar, so passing the inclusive
 * midnight straight through means:
 *   - a single-day booking (startDate === endDate) becomes start === end, i.e.
 *     a zero-width bar that renders as nothing (the missing T-01 bar in this
 *     ticket);
 *   - a multi-day booking stops one day short of its inclusive last day.
 *
 * Converting to the start of the *next* day (endDate + 1 day) makes a one-day
 * booking span exactly one day cell and a multi-day booking cover its full
 * inclusive range. Returns undefined for null/empty input (same contract as
 * toMs), so envelope rows with no end stay open-ended.
 */
export function toEndMs(isoDate: string | null | undefined): number | undefined {
  const ms = toMs(isoDate);
  return ms === undefined ? undefined : ms + DAY_MS;
}

/** Returns the earliest date string from an array; null if all null. */
export function minDate(...dates: (string | null | undefined)[]): string | null {
  const valid = dates.filter((d): d is string => !!d);
  if (!valid.length) return null;
  return valid.reduce((a, b) => (a < b ? a : b));
}

/** Returns the latest date string from an array; null if all null. */
export function maxDate(...dates: (string | null | undefined)[]): string | null {
  const valid = dates.filter((d): d is string => !!d);
  if (!valid.length) return null;
  return valid.reduce((a, b) => (a > b ? a : b));
}

/** Resolve booking eventType discriminator from raw API value. */
function toEventType(raw: string | null | undefined): GanttEventType {
  if (raw === 'Maintenance') return 'Maintenance';
  if (raw === 'Calibration') return 'Calibration';
  if (raw === 'OutOfService') return 'OutOfService';
  return null;
}

/** Resolve status discriminator from raw API value. */
function toStatus(raw: string | null | undefined): GanttStatus {
  if (raw === 'Tentative') return 'Tentative';
  if (raw === 'Committed') return 'Committed';
  return null;
}

/**
 * Opacity for a point — envelope/group rows get their own hard-coded opacity;
 * booking/assignment rows are handled by the pattern fill (opacity embedded in
 * the pattern tile itself) so we return 1 to let the pattern control opacity.
 */
export function resolveOpacity(taskType: GanttTaskType): number {
  switch (taskType) {
    case 'project':        return 0.3;
    case 'workOrder':      return 0.4;
    case 'machinesGroup':  return 0.25;
    case 'staffGroup':     return 0.25;
    // BUG-2026-0194: per-machine / per-person summary rows are the row's
    // content ("total work"), so they read stronger than the group bands but
    // still lighter than a real booking/assignment bar.
    case 'machineSummary': return 0.55;
    case 'personSummary':  return 0.55;
    // Booking/assignment rows: opacity is embedded in the pattern;
    // set point opacity to 1 so Highcharts doesn't double-apply it.
    default:               return 1.0;
  }
}

// ---------------------------------------------------------------------------
// Row builders
// ---------------------------------------------------------------------------

export function buildBookingPoint(
  bk: MasterGanttMachineBooking,
  parentId: string,
  colors: GanttColorTokens,
  /**
   * Optional Work Order context for the bar label. When supplied (machine-
   * grouped mode), the WO number is folded into the label so work-order
   * context stays visible even though the bar no longer sits under a WO row.
   * Does NOT change the bar's `id` or `custom` keys — only its display `name`.
   */
  woNumber?: string | null,
): GanttPoint {
  const base = bk.taskName
    ? `${bk.machineCode} — ${bk.taskName}`
    : `${bk.machineCode} (${bk.eventType ?? bk.source})`;
  const label = woNumber ? `${base} · ${woNumber}` : base;

  const taskType: GanttTaskType = 'machineBooking';
  const status: GanttStatus = toStatus(bk.status);
  const eventType: GanttEventType = toEventType(bk.eventType);
  // Run Mode Phase C: continuous (24/7) bookings take the continuous token so
  // a bar legitimately running through a weekend plotBand reads as its own
  // species (legend entry + tooltip explain the model).
  const fill: GanttFill = resolvePointFill(taskType, status, eventType, colors, bk.runMode ?? null);

  return {
    id: `booking-${bk.bookingKey}`,
    parent: parentId,
    name: label,
    start: toMs(bk.startDate),
    // BUG-2026-0095: inclusive endDate → exclusive end-of-day so single-day
    // bookings (startDate === endDate) get a one-day-wide, visible bar.
    end: toEndMs(bk.endDate),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status,
    eventType,
    custom: {
      bookingKey: bk.bookingKey,
      machineKey: bk.machineKey,
      status: bk.status,
      source: bk.source,
      eventType: bk.eventType,
      scheduleTaskKey: bk.scheduleTaskKey,
      // Phase C: the tooltip swaps to the continuous copy off this flag.
      runMode: bk.runMode ?? null,
      // Item 6.2 — Index-allocation bars carry these so the deep-link
      // resolver routes to the Scenario Planner Phase-3 tab instead of the
      // Machine Schedule (masterGanttDeepLinks.ts).
      isIndexAllocation: bk.isIndexAllocation ?? false,
      scenarioKey: bk.scenarioKey ?? null,
    },
  };
}

export function buildAssignmentPoint(
  ea: MasterGanttEmployeeAssignment,
  parentId: string,
  colors: GanttColorTokens,
  /**
   * Optional Work Order context for the bar label (machine-grouped mode).
   * Folded into the display `name` only — `id`/`custom` are unchanged.
   */
  woNumber?: string | null,
): GanttPoint {
  const base = ea.taskName
    ? `${ea.displayName} — ${ea.taskName}`
    : ea.displayName;
  // BUG-2026-0194: a below-100 allocation share ("· 10%") is the only stored
  // hint that this person is a reviewer/approver on the job — print it.
  const share = typeof ea.sharePercent === 'number' ? ea.sharePercent : null;
  const withShare = `${base}${shareSuffix(share, share)}`;
  const label = woNumber ? `${withShare} · ${woNumber}` : withShare;

  // Use explicit start/end if available; fall back to WeekStart → WeekStart+6
  const startMs = toMs(ea.startDate ?? ea.weekStart);
  const endDate = ea.endDate ?? addDays(ea.weekStart, 6);
  // BUG-2026-0095: inclusive endDate → exclusive end-of-day (see toEndMs) so a
  // single-day assignment is not collapsed to a zero-width bar.
  const endMs = toEndMs(endDate);

  const taskType: GanttTaskType = 'employeeAssignment';
  const status: GanttStatus = toStatus(ea.status);
  const eventType: GanttEventType = null; // assignments never have eventType
  const fill: GanttFill = resolvePointFill(taskType, status, eventType, colors);

  return {
    id: `assign-${ea.assignmentKey}`,
    parent: parentId,
    name: label,
    start: startMs,
    end: endMs,
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status,
    eventType,
    custom: {
      assignmentKey: ea.assignmentKey,
      userKey: ea.userKey,
      // BUG-2026-0194: the Employee Schedule deep-link (masterGanttDeepLinks)
      // needs the Saturday week start to focus the right week — it was never
      // copied here before, so "Open in Employee Schedule" could not be offered.
      weekStart: ea.weekStart,
      // Wave 3: percent is no longer shipped to the label paths — tooltip and
      // drawer read staffing as busy DAYS (the bar's span). The wire field
      // (ea.percent) stays on the payload for any other consumer.
      status: ea.status,
      source: ea.source,
      scheduleTaskKey: ea.scheduleTaskKey,
      // BUG-2026-0194: allocation share for the tooltip ("Share of job: 10%").
      sharePercent: share,
    },
  };
}

// ---------------------------------------------------------------------------
// Critical Path computation — Phase 4, Item 3
// ---------------------------------------------------------------------------

/**
 * One entry per project that has at least one Committed MachineBooking.
 * `date` is Unix milliseconds (UTC midnight) of the latest EndDate across
 * all Committed bookings in that project.
 */
export interface CriticalPathEntry {
  projectKey: string;
  projectName: string;
  /** Unix ms (UTC midnight) of the latest Committed booking end date. */
  date: number;
}

/**
 * Sentinel keys of the synthetic catch-all "projects" the Master Gantt invents
 * for bookings not tied to a real Project (Bucket B "Pending Quotes", Bucket C
 * "Manual Events", Bucket D "Manual Tasks"). Mirrors the *_PROJECT_KEY constants
 * in backend/src/services/masterGanttService.ts. These are NOT real projects, so
 * they get no critical-path / finish-line marker — a line labelled e.g.
 * "MANUAL: Manual Tasks" over a pile of unrelated manual tasks is meaningless.
 * Compared upper-cased to be robust to UUID casing.
 */
const SYNTHETIC_PROJECT_KEYS = new Set([
  '00000000-0000-0000-0000-00000000Q001', // Pending Quotes
  '00000000-0000-0000-0000-00000000M001', // Manual Events
  '00000000-0000-0000-0000-00000000M002', // Manual Tasks
]);

/**
 * For each project in `response`, find the maximum EndDate across all
 * MachineBooking entries whose status is exactly 'Committed'.
 *
 * Projects that have zero Committed bookings are excluded (no line to draw).
 *
 * Pure function — no side effects, fully unit-testable.
 *
 * @param response  API response from GET /api/schedule/master-gantt
 * @returns Array of critical-path entries, one per project with committed work.
 */
export function computeCriticalPathDates(
  response: MasterGanttResponse,
): CriticalPathEntry[] {
  const results: CriticalPathEntry[] = [];

  for (const proj of response.projects) {
    // Synthetic catch-all buckets (Pending Quotes / Manual Events / Manual Tasks)
    // are not real projects — no critical-path / finish-line marker for them.
    if (proj.projectKey && SYNTHETIC_PROJECT_KEYS.has(proj.projectKey.toUpperCase())) continue;
    let latestDate: string | null = null;

    for (const wo of proj.workOrders) {
      for (const bk of wo.machineBookings) {
        if (bk.status === 'Committed' && bk.endDate) {
          if (latestDate === null || bk.endDate > latestDate) {
            latestDate = bk.endDate;
          }
        }
      }
    }

    // Only include projects that have at least one Committed booking
    if (latestDate !== null) {
      const ms = toMs(latestDate);
      if (ms !== undefined) {
        results.push({
          projectKey: proj.projectKey,
          projectName: `${proj.projectId}: ${proj.projectName}`,
          date: ms,
        });
      }
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Top-level grouping mode for the Master Gantt.
 *   'project' — Project → WorkOrder → Machines/Staff (the original layout).
 *   'machine' — Machine (jobs + maintenance together) → People band
 *               (REQ-03: group by machine first, then by people).
 */
export type GanttGroupBy = 'project' | 'machine';

/**
 * Convert a MasterGanttResponse into Highcharts Gantt series data.
 *
 * Returns a single-series array (Highcharts treegrid works with one series
 * and `parent` linkage on each point for hierarchy).
 *
 * @param response  API response from GET /api/schedule/master-gantt
 * @param colors    Theme color tokens (from theme.schedule.*)
 * @param groupBy   Top-level grouping. Defaults to 'project' (original
 *                  behaviour, untouched). 'machine' pivots the SAME bookings/
 *                  assignments into a machine-first layout (REQ-03). Bar `id`
 *                  and `custom` keys are IDENTICAL across both modes, so
 *                  conflict tagging and deep-links keep working unchanged.
 */
/**
 * BUG-2026-0112-follow-up — Master Gantt crash on filter change.
 *
 * A stable signature of the treegrid ROW STRUCTURE: the set of node ids and
 * their parent links. Morphing the treegrid in place via `series.setData`
 * whenever this set changes (a Work Type / machine filter prunes whole
 * Project→WorkOrder→Machines/Staff branches) drives Highcharts' treegrid
 * `onBeforeRender` into a tick-label rebuild that dereferences a now-stale node
 * — "Cannot read properties of undefined (reading 'x')" in generateTick/
 * addLabel — blanking the page (same failure already fixed for the group-by
 * toggle via a remount key).
 *
 * The owning page keys <ScheduleGantt> on this signature so any STRUCTURAL
 * change forces a clean destroy+recreate, while pure attribute changes on the
 * SAME nodes (e.g. a status flip after bulk commit) keep the signature stable
 * and still flow through the imperative sync (animation + zoom preserved).
 */
export function ganttStructureSignature(points: Pick<GanttPoint, 'id' | 'parent'>[]): string {
  // Sorted so an incidental reorder alone never forces a remount.
  const tokens = points.map((p) => `${p.id}>${p.parent ?? ''}`).sort();
  const joined = tokens.join('|');
  // djb2 hash → compact, collision-safe enough for a remount key.
  let h = 5381;
  for (let i = 0; i < joined.length; i++) {
    h = (((h << 5) + h) ^ joined.charCodeAt(i)) >>> 0;
  }
  return `${points.length}-${h.toString(36)}`;
}

export function buildGanttChartData(
  response: MasterGanttResponse,
  colors: GanttColorTokens,
  groupBy: GanttGroupBy = 'project',
): GanttChartData {
  if (groupBy === 'machine') {
    return {
      series: [
        {
          name: 'Schedule',
          data: buildMachineGroupedPoints(response, colors),
        },
      ],
    };
  }

  const points: GanttPoint[] = [];

  for (const proj of response.projects) {
    points.push(...buildProjectPoints(proj, colors));
  }

  return {
    series: [
      {
        name: 'Schedule',
        data: points,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Internal utility (also used by builders above)
// ---------------------------------------------------------------------------

export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
